import { Equal, Folder, Rows01, Stars01 } from '#app/components/icons/duotone'
import { useState, useLayoutEffect, useRef } from 'react'
import { EmptyState } from '#app/components/application/empty-state/empty-state.js'
import { LoadingIndicator } from '#app/components/application/loading-indicator/loading-indicator.js'
import { EditIconSetModal } from '#app/components/application/modals/edit-icon-set-modal.js'
import { UpgradeModal } from '#app/components/application/modals/upgrade-modal.js'
import { Badge } from '#app/components/base/badges/badges.js'
import {
	ButtonGroup,
	ButtonGroupItem,
} from '#app/components/base/button-group/button-group.js'
import { Tooltip } from '#app/components/base/tooltip/tooltip.js'
import { IconGridBySearch, IconGridServer } from '#app/components/icon-grid.js'
import { MetaTags } from '#app/components/meta-tags.js'
import { SearchBar } from '#app/components/search-bar.js'
import { Button } from '#app/components/shared/buttons/button.js'
import { Select } from '#app/components/shared/select/select.js'
import { type DomainConfig } from '#app/config/domains.js'
import {
	cn,
	type DirectoryWithIcons,
	ICON_CARD_GRID_COLUMN_GAP,
} from '#app/utils/misc.js'
import {
	type Grouping,
	type IconFormat,
	type SearchType,
	type IconName,
} from '#app/utils/search-params.js'
import { formatResetTime } from '#app/utils/use-free-smart-search-limit.js'

// SSR directories - extended from DirectoryWithIcons with actualIconCount
interface SSRDirectory {
	path: string
	breadcrumbs: string[]
	icons: Array<{
		id: string
		name: string
		path: string
		svg: string
		color: string | null
		strokeWidth: number | null
	}>
	actualIconCount: number
}

interface PublicIconSetInfo {
	name: string
	slug: string
	author: string | null
	authorUrl: string | null
	license: string | null
}

interface UploadedSetInfo {
	id: string
	name: string
	slug: string
	count: number
}

export interface IconSetPageProps {
	title: string
	placeholder: string

	// Search
	query: string | null
	deferredQuery: string | null
	onQueryChange: (q: string | null) => void

	// Smart search
	searchType: SearchType
	onSearchTypeChange: (s: SearchType) => void
	isSmartSearchReady: boolean
	smartSearchProcessing: boolean

	// Format
	format: IconFormat

	// Grouping
	grouping: Grouping
	onGroupingChange: (g: Grouping) => void

	// Icon name
	iconName: IconName

	// Free smart search limit
	isLoggedIn?: boolean
	freeSearchesRemaining?: number | 'done'
	isFreeSearchExceeded?: boolean
	isFreeSearchHydrated?: boolean
	resetTime?: number | null

	// Grid
	isPending: boolean
	isEmptyIconSet?: boolean
	directoriesWithIcons: DirectoryWithIcons[]
	hasMoreIcons: boolean

	// SSR
	ssrDirectories: SSRDirectory[] | undefined
	ssrHasMoreIcons: boolean

	// Grid variant props
	publicIconSetsInfo?: PublicIconSetInfo[]
	uploadedId?: string
	uploadedSetsInfo?: UploadedSetInfo[]
	smartResults: Record<string, string[]> | undefined
	hasMoreSmartResults: boolean

	// Meta
	ogTitle: string
	ogDescription?: string
	domainConfig?: DomainConfig
	canonicalUrl?: string
}

export function IconSetPage({
	title,
	placeholder,
	query,
	deferredQuery,
	onQueryChange,
	searchType,
	onSearchTypeChange,
	isSmartSearchReady,
	smartSearchProcessing,
	format,
	grouping,
	onGroupingChange,
	iconName,
	isLoggedIn,
	freeSearchesRemaining,
	isFreeSearchExceeded,
	isFreeSearchHydrated,
	resetTime,
	isPending,
	isEmptyIconSet,
	directoriesWithIcons,
	hasMoreIcons,
	ssrDirectories,
	ssrHasMoreIcons,
	publicIconSetsInfo,
	uploadedId,
	uploadedSetsInfo,
	smartResults,
	hasMoreSmartResults,
	ogTitle,
	ogDescription,
	domainConfig,
	canonicalUrl,
}: IconSetPageProps) {
	// Domain-based default format
	const defaultFormat: IconFormat =
		domainConfig?.domainKey === 'notionicons' ? 'notion' : 'copy-svg'
	const resolvedFormat = format ?? defaultFormat

	const [stickyHeaderHeight, setStickyHeaderHeight] = useState<number>(0)
	const stickyHeaderRef = useRef<HTMLDivElement>(null)

	const [iconsGridContainerWidth, setIconsGridContainerWidth] = useState<
		number | null
	>(null)
	const iconsGridContainerRef = useRef<HTMLDivElement>(null)
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)

	// Set initial dimensions
	useLayoutEffect(() => {
		if (iconsGridContainerRef.current) {
			setIconsGridContainerWidth(iconsGridContainerRef.current.offsetWidth)
		}

		if (stickyHeaderRef.current) {
			setStickyHeaderHeight(stickyHeaderRef.current.offsetHeight)
		}

		const handleResize = () => {
			if (iconsGridContainerRef.current) {
				setIconsGridContainerWidth(iconsGridContainerRef.current.offsetWidth)
			}

			if (stickyHeaderRef.current) {
				setStickyHeaderHeight(stickyHeaderRef.current.offsetHeight)
			}
		}

		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	// Scroll to top on query change
	const hasRendered = useRef(false)
	useLayoutEffect(() => {
		if (hasRendered.current) {
			window.scrollTo({ top: 0 })
		}
		hasRendered.current = true
	}, [deferredQuery])

	const trimmedQuery = deferredQuery?.trim() ?? ''
	const showSmartMinLengthWarning =
		searchType === 'smart' && trimmedQuery.length > 0 && trimmedQuery.length < 3

	return (
		<>
			<MetaTags
				title={ogTitle}
				description={ogDescription}
				domainConfig={domainConfig}
				canonicalUrl={canonicalUrl}
			/>

			<h1 className="text-primary lg:text-display-xs text-xl font-semibold">
				{title}
			</h1>

			<div
				className="bg-primary sticky top-0 z-20 pt-[25px] pb-4"
				ref={stickyHeaderRef}
			>
				<div
					className="grid max-w-3xl grid-cols-[1fr_auto] items-start md:grid-cols-[1fr_1fr_auto] lg:grid-cols-[1fr_auto] 2xl:grid-cols-[1fr_1fr_auto]"
					style={{
						gap: `${ICON_CARD_GRID_COLUMN_GAP}px`,
					}}
				>
					<div className="col-span-2 md:col-span-1 lg:col-span-2 2xl:col-span-1">
						<SearchBar
							placeholder={placeholder}
							query={query ?? ''}
							setQuery={onQueryChange}
						/>
					</div>
					<Select
						size="md"
						aria-label="Search type"
						selectedKey={searchType}
						onSelectionChange={(key) => onSearchTypeChange(key as SearchType)}
						isDisabled={!isSmartSearchReady}
					>
						<Select.Item
							id="smart"
							icon={Stars01}
							label={
								smartSearchProcessing
									? 'Smart Search (processing...)'
									: 'Smart Search'
							}
						>
							<Badge size="sm" color="brand">
								Pro
							</Badge>
						</Select.Item>
						<Select.Item id="exact" icon={Equal}>
							Exact Search
						</Select.Item>
					</Select>

					<ButtonGroup
						size="lg"
						selectedKeys={new Set([grouping])}
						onSelectionChange={(keys) => {
							const [key] = keys
							if (!key || typeof key !== 'string') return
							onGroupingChange(key as Grouping)
						}}
					>
						<Tooltip title="Ungrouped">
							<ButtonGroupItem id="ungrouped" iconLeading={Rows01} />
						</Tooltip>
						<Tooltip title="Grouped">
							<ButtonGroupItem id="grouped" iconLeading={Folder} />
						</Tooltip>
					</ButtonGroup>
				</div>
				{!isLoggedIn &&
					searchType === 'smart' &&
					freeSearchesRemaining !== 'done' && (
						<p
							className={cn(
								'text-quaternary mt-1.5 text-sm',
								!isFreeSearchHydrated ? 'opacity-0' : '',
							)}
						>
							{freeSearchesRemaining} free smart searches remaining
							{resetTime && ` · Resets ${formatResetTime(resetTime)}`}
						</p>
					)}
			</div>

			<div className="relative isolate flex flex-1 flex-col">
				<div ref={iconsGridContainerRef}>
					{isFreeSearchExceeded && !isLoggedIn && searchType === 'smart' ? (
						<div className="flex flex-col items-center gap-4 py-20 text-center">
							<p className="text-secondary">
								Upgrade to unlock unlimited smart searches
							</p>
							{resetTime && (
								<p className="text-quaternary text-sm">
									Or wait until searches reset {formatResetTime(resetTime)}
								</p>
							)}
							{domainConfig && (
								<UpgradeModal
									trigger={<Button>Upgrade to Pro</Button>}
									domainConfig={domainConfig}
								/>
							)}
						</div>
					) : showSmartMinLengthWarning ? (
						<div className="py-5">
							<p className="text-danger">
								Type at least 3 characters for Smart search
							</p>
						</div>
					) : ssrDirectories && isPending ? (
						<IconGridServer
							directoriesWithIcons={ssrDirectories}
							stickyHeaderHeight={stickyHeaderHeight}
							query={deferredQuery ?? ''}
							hasMoreIcons={ssrHasMoreIcons}
							publicIconSetsInfo={publicIconSetsInfo}
							uploadedId={uploadedId}
							searchType={searchType}
							onSearchTypeChange={onSearchTypeChange}
							isSmartSearchReady={isSmartSearchReady}
							format={resolvedFormat}
							iconName={iconName}
						/>
					) : !isPending &&
					  iconsGridContainerWidth &&
					  directoriesWithIcons.length > 0 ? (
						<IconGridBySearch
							stickyHeaderHeight={stickyHeaderHeight}
							directoriesWithIcons={directoriesWithIcons}
							scrollMarginRef={iconsGridContainerRef}
							width={iconsGridContainerWidth}
							query={deferredQuery ?? ''}
							hasMoreIcons={hasMoreIcons}
							publicIconSetsInfo={publicIconSetsInfo}
							uploadedId={uploadedId}
							uploadedSetsInfo={uploadedSetsInfo}
							smartResults={smartResults}
							hasMoreSmartResults={hasMoreSmartResults}
							searchType={searchType}
							onSearchTypeChange={onSearchTypeChange}
							isSmartSearchReady={isSmartSearchReady}
							format={resolvedFormat}
							iconName={iconName}
						/>
					) : isPending ? (
						<div className="py-20">
							<LoadingIndicator
								size="xl"
								type="line-simple"
								label="Loading icons..."
							/>
						</div>
					) : isEmptyIconSet ? (
						<div className="py-20">
							<EmptyState>
								<EmptyState.Header>
									<EmptyState.Illustration type="box" />
								</EmptyState.Header>
								<EmptyState.Content>
									<EmptyState.Title>No icons uploaded</EmptyState.Title>
									<EmptyState.Description>
										Upload icons to this icon set to get started
									</EmptyState.Description>
								</EmptyState.Content>
								{uploadedId && (
									<EmptyState.Footer>
										<Button onClick={() => setIsEditModalOpen(true)}>
											Upload Icons
										</Button>
										<EditIconSetModal
											uploadedId={uploadedId}
											initialName={title}
											isOpen={isEditModalOpen}
											onOpenChange={setIsEditModalOpen}
										/>
									</EmptyState.Footer>
								)}
							</EmptyState>
						</div>
					) : (
						<div className="py-20 text-center">
							<p className="text-secondary">No icons found</p>
						</div>
					)}
				</div>
			</div>
		</>
	)
}
